import { formatCurrency } from "./detail/commissionDetailConstants";
import { resolveInstallmentRemainingTuition } from "./detail/commissionInstallmentUtils";
import {
  commissionGstTypeToInvoiceTaxType,
  DEFAULT_COMMISSION_GST_PERCENT,
} from "./detail/commissionPlanUtils";
import {
  calculateInvoiceLineTotals,
  formatInvoiceMoney,
  INVOICE_AMOUNT_MAX_DECIMALS,
  resolveLineItemTaxRate,
  resolveSavedLineTaxRate,
  resolveSavedLineTaxType,
} from "../../../utilis/invoiceLineItemHelpers";
import { resolveInvoiceLineItemsFromRecord } from "../../../utilis/invoiceEditHelpers";

function toSuggestionDate(value) {
  if (!value) return "";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Display dates like the invoice description sample: DD/MM/YYYY */
function formatDescriptionDate(value) {
  if (!value) return "";
  const iso = toSuggestionDate(value);
  if (!iso) return String(value).trim();
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return String(value).trim();
  return `${d}/${m}/${y}`;
}

function formatDescriptionMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return formatInvoiceMoney(num, "", {});
}

function toIdSet(values = []) {
  return new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => Number(value))
      .filter(Boolean),
  );
}

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

function resolveLinkedInvoice(row = {}) {
  return row.invoice || row.finance_invoice || row.financeInvoice || null;
}

/** Proportional paid share for a linked finance invoice line amount. */
export function resolveLinkedInvoicePaidShare(row = {}, lineAmount = 0) {
  const invoice = resolveLinkedInvoice(row);
  if (!invoice) return 0;

  const invoicePaid =
    Number(invoice.paid_amount ?? invoice.paidAmount ?? invoice.paid) || 0;
  const invoiceTotal =
    Number(invoice.total_amount ?? invoice.totalAmount ?? invoice.total) || 0;

  if (invoicePaid <= 0) return 0;
  if (invoiceTotal <= 0) return roundMoney(invoicePaid);

  const amount = Number(lineAmount) || 0;
  if (amount <= 0) return roundMoney(invoicePaid);

  return roundMoney(invoicePaid * (amount / invoiceTotal));
}

function resolveExplicitPaidAmount(row = {}, keys = []) {
  for (const key of keys) {
    const value = Number(row?.[key]) || 0;
    if (value > 0) return value;
  }
  return 0;
}

function resolveInvoiceRecordStatus(invoice = {}) {
  return String(
    invoice.status ?? invoice.payment_status ?? invoice.raw?.status ?? "",
  ).toLowerCase();
}

function isActiveInvoiceStatus(status = "") {
  const normalized = String(status || "").toLowerCase();
  return !["cancelled", "void", "refunded"].includes(normalized);
}

function resolveSuggestionLineAmount(line = {}) {
  const commissionId = Number(line?.commission_installment_id);
  const bonusId = Number(line?.bonus_installment_id);
  const unitPrice = Number(line?.unit_price ?? line?.unitPrice) || 0;
  const qty = Number(line?.quantity ?? line?.qty) || 1;
  const discount = Number(line?.discount ?? line?.discount_amount) || 0;

  if (commissionId) {
    return roundMoney(unitPrice);
  }

  if (bonusId) {
    return roundMoney(unitPrice > 0 ? unitPrice : unitPrice * qty - discount);
  }

  const lineTotal = Number(line?.line_total ?? line?.amount) || 0;
  if (lineTotal > 0) return roundMoney(lineTotal);

  return roundMoney(Math.max(0, unitPrice * qty - discount));
}

function resolveInquiryFeeTypeFromLine(line = {}) {
  const feeType = String(line?.fee_type ?? line?.feeType ?? "").toLowerCase();
  if (feeType === "application_fee" || feeType === "material_fee") {
    return feeType;
  }

  const description = String(line?.description ?? line?.item_name ?? "")
    .trim()
    .toLowerCase();
  if (
    description === "application fee" ||
    description.includes("application fee")
  ) {
    return "application_fee";
  }
  if (description === "material fee" || description.includes("material fee")) {
    return "material_fee";
  }

  return "";
}

/**
 * Sum amounts already on commission invoices (any status except cancelled/void).
 * Based on invoice line prices only — not payment status.
 */
export function buildInvoicedAllocationMapsFromCommissionInvoices(
  invoices = [],
  { excludeInvoiceId = null } = {},
) {
  const commissionTuitionById = {};
  const bonusAmountById = {};
  let applicationFeeInvoiced = 0;
  let materialFeeInvoiced = 0;

  const addCommissionTuition = (installmentId, amount) => {
    const id = Number(installmentId);
    const value = Number(amount) || 0;
    if (!id || value <= 0) return;
    commissionTuitionById[id] = roundMoney(
      (commissionTuitionById[id] || 0) + value,
    );
  };

  const addBonusAmount = (installmentId, amount) => {
    const id = Number(installmentId);
    const value = Number(amount) || 0;
    if (!id || value <= 0) return;
    bonusAmountById[id] = roundMoney((bonusAmountById[id] || 0) + value);
  };

  (Array.isArray(invoices) ? invoices : []).forEach((invoiceRow) => {
    const invoiceRecord = invoiceRow?.raw || invoiceRow?.wrapper || invoiceRow;
    const invoiceId =
      invoiceRow?.id ??
      invoiceRow?.financeInvoiceId ??
      invoiceRecord?.id ??
      null;

    if (
      excludeInvoiceId &&
      invoiceId != null &&
      String(invoiceId) === String(excludeInvoiceId)
    ) {
      return;
    }

    const status = String(
      invoiceRow?.status ?? invoiceRecord?.status ?? "",
    ).toLowerCase();
    if (!isActiveInvoiceStatus(status)) {
      return;
    }

    const recordLines = resolveInvoiceLineItemsFromRecord(invoiceRecord);
    const wrapperLines =
      invoiceRow?.wrapper && invoiceRow.wrapper !== invoiceRecord
        ? resolveInvoiceLineItemsFromRecord(invoiceRow.wrapper)
        : [];
    const lines =
      wrapperLines.length > recordLines.length ? wrapperLines : recordLines;
    lines.forEach((line) => {
      const commissionId = Number(
        line?.commission_installment_id ?? line?.commissionInstallmentId,
      );
      const bonusId = Number(line?.bonus_installment_id);

      if (commissionId) {
        addCommissionTuition(
          commissionId,
          Number(line?.unit_price ?? line?.unitPrice) || 0,
        );
        return;
      }

      if (bonusId) {
        addBonusAmount(
          bonusId,
          Number(line?.unit_price ?? line?.unitPrice) || 0,
        );
        return;
      }

      const inquiryFeeType = resolveInquiryFeeTypeFromLine(line);
      const amount = resolveSuggestionLineAmount(line);
      if (amount <= 0) return;
      if (inquiryFeeType === "application_fee") {
        applicationFeeInvoiced += amount;
      } else if (inquiryFeeType === "material_fee") {
        materialFeeInvoiced += amount;
      }
    });

    // Fallback only when line items lack installment ids — use actual line price, never plan tuition.
    const linkedCommissionInstallments = Array.isArray(
      invoiceRecord?.linked_installments,
    )
      ? invoiceRecord.linked_installments
      : Array.isArray(invoiceRow?.linkedInstallments)
        ? invoiceRow.linkedInstallments
        : [];

    linkedCommissionInstallments.forEach((inst) => {
      const id = Number(inst?.id);
      if (!id || commissionTuitionById[id] > 0) return;

      const itemId = Number(
        inst?.finance_invoice_item_id ?? inst?.financeInvoiceItemId,
      );
      const line = itemId
        ? lines.find((row) => Number(row?.id) === itemId)
        : lines.find((row) => Number(row?.commission_installment_id) === id);

      if (line) {
        addCommissionTuition(
          id,
          Number(line?.unit_price ?? line?.unitPrice) || 0,
        );
        return;
      }

      const linkedUsed = Number(
        inst?.tuition_allocated ??
          inst?.tuitionAllocated ??
          inst?.unit_price ??
          inst?.unitPrice ??
          inst?.finance_invoice_item?.unit_price ??
          inst?.invoice_item?.unit_price ??
          inst?.pivot?.unit_price ??
          inst?.pivot?.tuition_allocated ??
          0,
      );
      if (linkedUsed > 0) addCommissionTuition(id, linkedUsed);
    });

    const linkedBonusInstallments = Array.isArray(
      invoiceRecord?.linked_bonus_installments,
    )
      ? invoiceRecord.linked_bonus_installments
      : Array.isArray(invoiceRow?.linkedBonusInstallments)
        ? invoiceRow.linkedBonusInstallments
        : [];

    linkedBonusInstallments.forEach((inst) => {
      const id = Number(inst?.id);
      if (!id || bonusAmountById[id] > 0) return;

      const itemId = Number(
        inst?.finance_invoice_item_id ?? inst?.financeInvoiceItemId,
      );
      const line = itemId
        ? lines.find((row) => Number(row?.id) === itemId)
        : lines.find((row) => Number(row?.bonus_installment_id) === id);

      if (line) {
        addBonusAmount(id, Number(line?.unit_price ?? line?.unitPrice) || 0);
      }
    });
  });

  return {
    commissionTuitionById,
    bonusAmountById,
    applicationFeeInvoiced: roundMoney(applicationFeeInvoiced),
    materialFeeInvoiced: roundMoney(materialFeeInvoiced),
    excludeInvoiceId,
  };
}

/** Parse plan/installment commission rate as a percent (40 for 40%). */
export function parseCommissionRatePercent(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value).trim().replace(/%/g, "");
  const num = Number(text);
  return Number.isFinite(num) ? num : 0;
}

/** 40% → "0.4", 35% → "0.35" */
export function commissionRateToQuantity(ratePercent) {
  const rate = parseCommissionRatePercent(ratePercent);
  if (!Number.isFinite(rate) || rate <= 0) return "";
  const qty = rate / 100;
  return String(Number(qty.toFixed(INVOICE_AMOUNT_MAX_DECIMALS)));
}

function resolveCommissionQuantity(row = {}, defaultCommissionRate) {
  const fromRate = commissionRateToQuantity(
    row.commissionRate ?? defaultCommissionRate,
  );
  if (fromRate) return fromRate;

  const tuition = Number(row.tuitionAmount) || 0;
  const commission = Number(row.commissionAmount) || 0;
  if (tuition > 0 && commission > 0) {
    return String(
      Number((commission / tuition).toFixed(INVOICE_AMOUNT_MAX_DECIMALS)),
    );
  }
  return "1";
}

function listRowFinanceAllocations(row = {}) {
  const list = row.financeAllocations ?? row.finance_allocations ?? [];
  return Array.isArray(list) ? list : [];
}

function resolveInvoiceIdFromAllocation(alloc = {}) {
  return Number(
    alloc.financeInvoiceId ??
      alloc.finance_invoice_id ??
      alloc.invoice?.id ??
      0,
  );
}

function sumLineTuitionForInstallment(
  lineItems = [],
  installmentId,
  editingRowId,
) {
  const id = Number(installmentId);
  if (!id) return 0;
  return roundMoney(
    (Array.isArray(lineItems) ? lineItems : []).reduce((sum, line) => {
      if (editingRowId && line?.id === editingRowId) return sum;
      if (Number(line?.commission_installment_id) !== id) return sum;
      return sum + (Number(line?.unit_price) || 0);
    }, 0),
  );
}

function sumLineCommissionForInstallment(
  lineItems = [],
  installmentId,
  editingRowId,
) {
  const id = Number(installmentId);
  if (!id) return 0;
  return roundMoney(
    (Array.isArray(lineItems) ? lineItems : []).reduce((sum, line) => {
      if (editingRowId && line?.id === editingRowId) return sum;
      if (Number(line?.commission_installment_id) !== id) return sum;
      const tuition = Number(line?.unit_price) || 0;
      const qty = Number(line?.quantity) || 0;
      return sum + tuition * qty;
    }, 0),
  );
}

function sumLineBonusForInstallment(
  lineItems = [],
  bonusInstallmentId,
  editingRowId,
) {
  const id = Number(bonusInstallmentId);
  if (!id) return 0;
  return roundMoney(
    (Array.isArray(lineItems) ? lineItems : []).reduce((sum, line) => {
      if (editingRowId && line?.id === editingRowId) return sum;
      if (Number(line?.bonus_installment_id) !== id) return sum;
      const unit = Number(line?.unit_price) || 0;
      const qty = Number(line?.quantity) || 1;
      return sum + unit * qty;
    }, 0),
  );
}

/**
 * Remaining tuition still available to put on invoice lines.
 * Uses finance allocations on other invoices + unsaved lines on the current invoice.
 */
export function resolveRemainingTuition(row = {}, invoiceContext = null) {
  const tuitionCap = Number(row.tuitionAmount ?? row.tuition_amount) || 0;
  if (tuitionCap <= 0) return 0;

  const ctx = invoiceContext || {};
  const currentInvoiceId = Number(ctx.currentInvoiceId) || 0;
  const lineItems = ctx.lineItems || [];
  const editingRowId = ctx.editingRowId ?? null;
  const installmentId = Number(row.id);

  let usedOnOtherInvoices = 0;
  for (const alloc of listRowFinanceAllocations(row)) {
    const invoiceId = resolveInvoiceIdFromAllocation(alloc);
    if (currentInvoiceId > 0 && invoiceId === currentInvoiceId) continue;
    usedOnOtherInvoices +=
      Number(alloc.tuitionAllocated ?? alloc.tuition_allocated) || 0;
  }

  const usedOnCurrentInvoice = sumLineTuitionForInstallment(
    lineItems,
    installmentId,
    editingRowId,
  );

  return Math.max(
    0,
    roundMoney(tuitionCap - usedOnOtherInvoices - usedOnCurrentInvoice),
  );
}

/** Remaining commission still available to allocate on invoice lines. */
export function resolveRemainingCommission(row = {}, invoiceContext = null) {
  const commissionCap =
    Number(row.commissionAmount ?? row.commission_amount) ||
    Number(row.invoiceAmount ?? row.invoice_amount) ||
    0;
  if (commissionCap <= 0) return 0;

  const ctx = invoiceContext || {};
  const currentInvoiceId = Number(ctx.currentInvoiceId) || 0;
  const lineItems = ctx.lineItems || [];
  const editingRowId = ctx.editingRowId ?? null;
  const installmentId = Number(row.id);

  let usedOnOtherInvoices = 0;
  for (const alloc of listRowFinanceAllocations(row)) {
    const invoiceId = resolveInvoiceIdFromAllocation(alloc);
    if (currentInvoiceId > 0 && invoiceId === currentInvoiceId) continue;
    usedOnOtherInvoices +=
      Number(alloc.allocatedAmount ?? alloc.allocated_amount) || 0;
  }

  const usedOnCurrentInvoice = sumLineCommissionForInstallment(
    lineItems,
    installmentId,
    editingRowId,
  );

  return Math.max(
    0,
    roundMoney(commissionCap - usedOnOtherInvoices - usedOnCurrentInvoice),
  );
}

/** Remaining bonus still available to allocate on invoice lines. */
export function resolveRemainingBonus(row = {}, invoiceContext = null) {
  const bonusCap = Number(row.bonusAmount ?? row.bonus_amount) || 0;
  if (bonusCap <= 0) return 0;

  const ctx = invoiceContext || {};
  const currentInvoiceId = Number(ctx.currentInvoiceId) || 0;
  const lineItems = ctx.lineItems || [];
  const editingRowId = ctx.editingRowId ?? null;
  const bonusInstallmentId = Number(row.id);

  let usedOnOtherInvoices = 0;
  for (const alloc of listRowFinanceAllocations(row)) {
    const invoiceId = resolveInvoiceIdFromAllocation(alloc);
    if (currentInvoiceId > 0 && invoiceId === currentInvoiceId) continue;
    usedOnOtherInvoices +=
      Number(alloc.allocatedAmount ?? alloc.allocated_amount) || 0;
  }

  const usedOnCurrentInvoice = sumLineBonusForInstallment(
    lineItems,
    bonusInstallmentId,
    editingRowId,
  );

  return Math.max(
    0,
    roundMoney(bonusCap - usedOnOtherInvoices - usedOnCurrentInvoice),
  );
}

export function installmentHasRemainingToInvoice(
  row = {},
  invoiceContext = null,
) {
  if (!row?.id) return false;
  const status = String(row.status || "").toLowerCase();
  if (status === "cancelled" || status === "void") return false;

  const remainingTuition = resolveRemainingTuition(row, invoiceContext);
  const remainingCommission = resolveRemainingCommission(row, invoiceContext);
  if (remainingTuition > 0.009 || remainingCommission > 0.009) return true;

  return false;
}

export function bonusInstallmentHasRemainingToInvoice(
  row = {},
  invoiceContext = null,
) {
  if (!row?.id) return false;
  const status = String(row.status || "").toLowerCase();
  if (status === "cancelled" || status === "void") return false;

  const remaining = resolveRemainingBonus(row, invoiceContext);
  return remaining > 0.009;
}

/**
 * Multi-line description for a commission (tuition) installment only.
 * Commission line shows the rate label only (no commission amount).
 */
export function buildCommissionInvoiceLineDescription({
  studentName = "",
  studentId = "",
  studentDob = "",
  courseName = "",
  startDate = "",
  endDate = "",
  tuitionFee,
  commissionRate,
} = {}) {
  const rate = Number(commissionRate);
  const rateLabel = Number.isFinite(rate) && rate > 0 ? ` ${rate}%` : "";

  return [
    `Student Name: ${studentName || "—"}`,
    `Student ID: ${studentId || "—"}`,
    `DOB: ${formatDescriptionDate(studentDob) || "—"}`,
    `Course Name: ${courseName || "—"}`,
    `Start Date: ${formatDescriptionDate(startDate) || "—"}`,
    `End Date: ${formatDescriptionDate(endDate) || "—"}`,
    `Tuition Fee: ${formatDescriptionMoney(tuitionFee)}`,
    `Commission${rateLabel}:`,
  ].join("\n");
}

/** Multi-line description for a bonus installment. */
export function buildBonusInvoiceLineDescription({
  studentName = "",
  studentId = "",
  studentDob = "",
  courseName = "",
  providerName = "",
  installmentNo = "",
  dueDate = "",
  bonusAmount = "",
} = {}) {
  return [
    `Student Name: ${studentName || "—"}`,
    `Student ID: ${studentId || "—"}`,
    `DOB: ${formatDescriptionDate(studentDob) || "—"}`,
    `Course Name: ${courseName || "—"}`,
    `Provider: ${providerName || "—"}`,
    `Bonus Installment: #${installmentNo || "—"}`,
    `Due Date: ${formatDescriptionDate(dueDate) || "—"}`,
    `Bonus Amount: ${formatDescriptionMoney(bonusAmount)}`,
  ].join("\n");
}

const INQUIRY_FEE_TYPES = new Set(["application_fee", "material_fee"]);

export function isInquiryFeeSuggestionType(feeType) {
  return INQUIRY_FEE_TYPES.has(String(feeType || "").toLowerCase());
}

/** Remaining inquiry fee amount still billable on a new invoice line. */
export function resolveBillableInquiryFeeRemaining(
  planned = 0,
  invoiced = 0,
  explicitRemaining = null,
) {
  const target = Number(planned) || 0;
  const billed = Number(invoiced) || 0;
  const computed = Math.max(0, roundMoney(target - billed));
  if (explicitRemaining == null || explicitRemaining === "") {
    return computed;
  }
  const explicit = Math.max(0, Number(explicitRemaining) || 0);
  if (explicit > 0.009) {
    return explicit;
  }
  if (target > 0.009 && computed > explicit + 0.009) {
    return computed;
  }
  return explicit;
}

/**
 * Record-only inquiry fees (application + material) for commission invoice lines.
 */
export function buildInquiryFeeSuggestions({
  applicationId,
  applicationType = "education",
  currency = "AUD",
  applicationFee = 0,
  materialFee = 0,
  applicationFeePaid = 0,
  materialFeePaid = 0,
  applicationFeeInvoiced = 0,
  materialFeeInvoiced = 0,
  applicationFeeRemaining = null,
  materialFeeRemaining = null,
} = {}) {
  const options = [];
  const appFee = Number(applicationFee) || 0;
  const matFee = Number(materialFee) || 0;
  const appInvoiced = Number(applicationFeeInvoiced) || 0;
  const matInvoiced = Number(materialFeeInvoiced) || 0;
  const appRemaining = resolveBillableInquiryFeeRemaining(
    appFee,
    appInvoiced,
    applicationFeeRemaining,
  );
  const matRemaining = resolveBillableInquiryFeeRemaining(
    matFee,
    matInvoiced,
    materialFeeRemaining,
  );

  if (appRemaining > 0.009) {
    const amountLabel =
      appInvoiced > 0.009 && appFee > appRemaining
        ? `Remaining ${formatCurrency(appRemaining, currency)} of ${formatCurrency(appFee, currency)}`
        : formatCurrency(appRemaining, currency);
    options.push({
      id: "inquiry-application-fee",
      label: `Application Fee · ${amountLabel}`,
      displayTitle: "Application fee",
      displaySubtitle:
        appInvoiced > 0.009 && appFee > appRemaining
          ? `Remaining to invoice · total ${formatCurrency(appFee, currency)}`
          : "Inquiry fee",
      displayAmount: formatCurrency(appRemaining, currency),
      fields: {
        description: "Application Fee",
        unit_price: String(appRemaining),
        quantity: "1",
        unit_price_locked: false,
        quantity_locked: true,
        tax_type: "notax",
        tax_rate: "",
        currency,
        fee_type: "application_fee",
        application_id: applicationId || null,
        application_type: applicationType || "",
        commission_installment_id: null,
        bonus_installment_id: null,
      },
    });
  }

  if (matRemaining > 0.009) {
    const amountLabel =
      matInvoiced > 0.009 && matFee > matRemaining
        ? `Remaining ${formatCurrency(matRemaining, currency)} of ${formatCurrency(matFee, currency)}`
        : formatCurrency(matRemaining, currency);
    options.push({
      id: "inquiry-material-fee",
      label: `Material Fee · ${amountLabel}`,
      displayTitle: "Material fee",
      displaySubtitle:
        matInvoiced > 0.009 && matFee > matRemaining
          ? `Remaining to invoice · total ${formatCurrency(matFee, currency)}`
          : "Inquiry fee",
      displayAmount: formatCurrency(matRemaining, currency),
      fields: {
        description: "Material Fee",
        unit_price: String(matRemaining),
        quantity: "1",
        unit_price_locked: false,
        quantity_locked: true,
        tax_type: "notax",
        tax_rate: "",
        currency,
        fee_type: "material_fee",
        application_id: applicationId || null,
        application_type: applicationType || "",
        commission_installment_id: null,
        bonus_installment_id: null,
      },
    });
  }

  return options;
}

/**
 * Align saved invoice lines with installment linkage metadata.
 * By default preserves saved unit_price / qty / tax from the invoice
 * (edit must show what was saved, e.g. 300 not installment 750).
 * Pass overwriteAmountsFromInstallments=true only for create-time rebuilds.
 */
export function syncCommissionInvoiceLinesWithInstallments(
  lineItems = [],
  commissionInstallments = [],
  bonusInstallments = [],
  {
    defaultCommissionRate = "",
    commissionType = "percentage",
    overwriteAmountsFromInstallments = false,
  } = {},
) {
  const commissionById = new Map(
    (Array.isArray(commissionInstallments) ? commissionInstallments : [])
      .filter((row) => row?.id != null)
      .map((row) => [Number(row.id), row]),
  );
  const bonusById = new Map(
    (Array.isArray(bonusInstallments) ? bonusInstallments : [])
      .filter((row) => row?.id != null)
      .map((row) => [Number(row.id), row]),
  );

  return (Array.isArray(lineItems) ? lineItems : []).map((line) => {
    const commissionId = Number(
      line?.commission_installment_id ?? line?.commissionInstallmentId,
    );
    const bonusId = Number(
      line?.bonus_installment_id ?? line?.bonusInstallmentId,
    );

    if (commissionId && commissionById.has(commissionId)) {
      const installment = commissionById.get(commissionId);
      if (!overwriteAmountsFromInstallments) {
        return {
          ...line,
          commission_installment_id: commissionId,
          unit_price_locked: false,
          quantity_locked: true,
        };
      }
      const fixedPlan = String(commissionType || "").toLowerCase() === "fixed";
      const tuitionAmount = Number(installment.tuitionAmount) || 0;
      const fixedAmount = Number(installment.commissionAmount) || 0;
      const quantity = fixedPlan
        ? "1"
        : resolveCommissionQuantity(installment, defaultCommissionRate);
      return {
        ...line,
        commission_installment_id: commissionId,
        unit_price: fixedPlan ? String(fixedAmount) : String(tuitionAmount),
        quantity: String(quantity || "1"),
        unit_price_locked: false,
        quantity_locked: true,
        tax_type: "notax",
        tax_rate: "",
        gst_status: "notax",
        gst_percentage: 0,
      };
    }

    if (bonusId && bonusById.has(bonusId)) {
      const installment = bonusById.get(bonusId);
      if (!overwriteAmountsFromInstallments) {
        return {
          ...line,
          bonus_installment_id: bonusId,
          unit_price_locked: false,
          quantity_locked: true,
        };
      }
      const amount = Number(installment.bonusAmount) || 0;
      const bonusTaxType = commissionGstTypeToInvoiceTaxType(
        installment.gstType || installment.gst_type,
      );
      const bonusTaxRate = resolveLineItemTaxRate(
        bonusTaxType,
        DEFAULT_COMMISSION_GST_PERCENT,
      );
      return {
        ...line,
        bonus_installment_id: bonusId,
        unit_price: String(amount),
        quantity: "1",
        unit_price_locked: false,
        quantity_locked: true,
        tax_type: bonusTaxType,
        tax_rate: bonusTaxRate,
        gst_status: bonusTaxType,
        gst_percentage: bonusTaxRate ? Number(bonusTaxRate) : 0,
      };
    }

    return line;
  });
}

/** Recalculate invoice money fields from commission line items. */
export function applyCommissionInvoiceLineTotals(
  invoice,
  commissionInstallments = [],
  bonusInstallments = [],
  {
    defaultCommissionRate = "",
    commissionType = "percentage",
    overwriteAmountsFromInstallments = false,
  } = {},
) {
  if (!invoice) return invoice;

  const line_items = syncCommissionInvoiceLinesWithInstallments(
    invoice.line_items || invoice.items || [],
    commissionInstallments,
    bonusInstallments,
    { defaultCommissionRate, commissionType, overwriteAmountsFromInstallments },
  );

  // Edit load: keep saved invoice totals / tax / discount from the API.
  if (!overwriteAmountsFromInstallments) {
    return {
      ...invoice,
      line_items,
      items: line_items,
    };
  }

  const totals = calculateInvoiceLineTotals(
    line_items.map((row, index) => ({
      id: row.id || `sync-line-${index}`,
      description: row.description || "",
      quantity: row.quantity ?? row.qty ?? 1,
      unit_price: row.unit_price ?? row.unitPrice ?? 0,
      discount: row.discount ?? 0,
      tax_type: row.tax_type || row.gst_status || "notax",
      tax_rate: row.tax_rate ?? row.gst_percentage ?? 0,
    })),
  );

  const paid = Math.max(
    Number(invoice.paid_amount ?? invoice.paidAmount) || 0,
    0,
  );
  const totalAmount = Number(totals.total_amount) || 0;

  return {
    ...invoice,
    line_items,
    items: line_items,
    gross_total: totals.gross_subtotal,
    subtotal: totals.subtotal,
    tax_amount: totals.tax_amount,
    total_amount: totalAmount,
    totalAmount,
    amount_due: Math.max(totalAmount - paid, 0),
    amountDue: Math.max(totalAmount - paid, 0),
  };
}

/**
 * Build description-picker options from commission / bonus installments that
 * still have remaining tuition or commission to invoice (includes partials).
 * Commission lines: unit price = remaining tuition, qty = commission rate as decimal.
 */
export function buildInstallmentSuggestions({
  commissionInstallments = [],
  bonusInstallments = [],
  applicationId,
  applicationType = "education",
  currency = "AUD",
  courseName = "",
  providerName = "",
  studentName = "",
  studentId = "",
  studentDob = "",
  startDate = "",
  endDate = "",
  /** Plan-level rate used when an installment row has no commissionRate. */
  defaultCommissionRate = "",
  /** "fixed" bills the calculated amount. Anything else keeps tuition × rate. */
  commissionType = "percentage",
  applicationFee = 0,
  materialFee = 0,
  applicationFeeInvoiced = 0,
  materialFeeInvoiced = 0,
  applicationFeeRemaining = null,
  materialFeeRemaining = null,
  includeInquiryFees = true,
  invoiceContext = null,
  excludeCommissionIds = [],
  excludeBonusIds = [],
  commissionInvoices = [],
  excludeInvoiceId = null,
} = {}) {
  const excludeCommission = toIdSet(excludeCommissionIds);
  const excludeBonus = toIdSet(excludeBonusIds);
  const allocations = buildInvoicedAllocationMapsFromCommissionInvoices(
    commissionInvoices,
    { excludeInvoiceId },
  );

  const invoicedTuitionById = allocations.commissionTuitionById || {};
  const suggestionRemainingForRow = (row, fixedPlan) => {
    const cap = fixedPlan
      ? Number(row.commissionAmount) || Number(row.invoiceAmount) || 0
      : Number(row.tuitionAmount) || 0;
    if (cap <= 0) return 0;

    const invoiced = invoicedTuitionById[Number(row.id)] || 0;
    const editingRowId = invoiceContext?.editingRowId ?? null;
    const currentUsed = roundMoney(
      (invoiceContext?.lineItems || []).reduce((sum, line) => {
        if (editingRowId && line?.id === editingRowId) return sum;
        if (Number(line?.commission_installment_id) !== Number(row.id)) {
          return sum;
        }
        return sum + (Number(line?.unit_price ?? line?.unitPrice) || 0);
      }, 0),
    );
    const fromInvoices = Math.max(
      0,
      roundMoney(cap - invoiced - currentUsed),
    );
    const fromAllocations = fixedPlan
      ? resolveRemainingCommission(row, invoiceContext)
      : Math.min(
          resolveRemainingTuition(row, invoiceContext),
          // Create-invoice has no current invoice. Installment allocations
          // already store partial use (tuition, or commission scaled back to tuition).
          invoiceContext?.currentInvoiceId
            ? Number.POSITIVE_INFINITY
            : resolveInstallmentRemainingTuition(row),
        );

    // Invoice line prices are the amount actually used (5000 used → hide,
    // 3000 of 5000 → 2000). Allocations cover the case where lines are absent.
    if (invoiced > 0.009) return fromInvoices;
    return Math.min(fromAllocations, fromInvoices);
  };

  const commissionOptions = (
    Array.isArray(commissionInstallments) ? commissionInstallments : []
  )
    .filter((row) => {
      if (!row?.id || excludeCommission.has(Number(row.id))) return false;
      const status = String(row.status || "").toLowerCase();
      if (status === "cancelled" || status === "void") return false;
      const fixedPlan = String(commissionType || "").toLowerCase() === "fixed";
      return suggestionRemainingForRow(row, fixedPlan) > 0.009;
    })
    .map((row) => {
      const tuitionAmount = Number(row.tuitionAmount) || 0;
      const commissionAmount = Number(row.commissionAmount) || 0;
      const fixedPlan = String(commissionType || "").toLowerCase() === "fixed";
      const remainingForSuggestion = suggestionRemainingForRow(row, fixedPlan);
      const remainingTuition = fixedPlan
        ? resolveRemainingTuition(row, invoiceContext)
        : remainingForSuggestion;
      const remainingCommission = fixedPlan
        ? remainingForSuggestion
        : resolveRemainingCommission(row, invoiceContext);
      const suggestTuition =
        remainingTuition > 0.009 ? remainingTuition : tuitionAmount;
      const rate = fixedPlan
        ? 0
        : parseCommissionRatePercent(row.commissionRate) ||
          parseCommissionRatePercent(defaultCommissionRate);
      const quantity = fixedPlan
        ? "1"
        : resolveCommissionQuantity(row, defaultCommissionRate);
      const fixedBillable = fixedPlan
        ? remainingCommission > 0.009
          ? remainingCommission
          : commissionAmount
        : 0;
      const discountPercent = Number(
        row.studentDiscountPercent ?? row.student_discount_percent,
      );
      const due = row.dueDate || "—";
      const isPartial =
        remainingTuition + 0.009 < tuitionAmount && tuitionAmount > 0;
      const amountLabel = isPartial
        ? `Remaining ${formatCurrency(suggestTuition, currency)} of ${formatCurrency(tuitionAmount, currency)}`
        : formatCurrency(suggestTuition, currency);
      const commissionSubtitleParts = [`Installment #${row.installmentNo}`];
      if (due && due !== "—") commissionSubtitleParts.push(`Due ${due}`);
      if (fixedPlan) {
        commissionSubtitleParts.push(
          `Fixed ${formatCurrency(fixedBillable, currency)}`,
        );
      } else if (rate) commissionSubtitleParts.push(`${rate}% commission`);
      else if (remainingCommission > 0.009) {
        commissionSubtitleParts.push(
          `Commission left ${formatCurrency(remainingCommission, currency)}`,
        );
      } else if (commissionAmount > 0.009) {
        commissionSubtitleParts.push(
          `Commission ${formatCurrency(commissionAmount, currency)}`,
        );
      }

      return {
        id: `commission-${row.id}`,
        label: `Tuition #${row.installmentNo} · Due ${due} · ${amountLabel}${
          fixedPlan
            ? ` · fixed ${formatCurrency(fixedBillable, currency)}`
            : rate
              ? ` · ${rate}% → qty ${quantity}`
              : remainingCommission
              ? ` (comm. left ${formatCurrency(remainingCommission, currency)})`
              : commissionAmount
                ? ` (comm. ${formatCurrency(commissionAmount, currency)})`
                : ""
        }`,
        displayTitle: "Commissionable fee",
        displaySubtitle: commissionSubtitleParts.join(" · "),
        displayAmount: formatCurrency(
          fixedPlan ? fixedBillable : suggestTuition,
          currency,
        ),
        displayAmountNote:
          isPartial && tuitionAmount > 0
            ? `of ${formatCurrency(tuitionAmount, currency)}`
            : "",
        fields: {
          description: buildCommissionInvoiceLineDescription({
            studentName,
            studentId,
            studentDob,
            courseName,
            startDate,
            endDate,
            tuitionFee: suggestTuition,
            commissionRate: fixedPlan ? "" : rate,
          }),
          // Percentage: line total = remaining tuition × (rate/100).
          // Fixed: line total = calculated commission amount, qty 1.
          unit_price: fixedPlan ? String(fixedBillable) : String(suggestTuition),
          quantity: String(quantity || "1"),
          unit_price_locked: false,
          quantity_locked: true,
          tax_type: "notax",
          tax_rate: "",
          currency,
          fee_type: "tuition",
          application_id: applicationId || null,
          application_type: applicationType || "",
          commission_installment_id: row.id,
          bonus_installment_id: null,
          invoice_due_date: toSuggestionDate(row.dueDate),
          discount_percent:
            Number.isFinite(discountPercent) && discountPercent > 0
              ? String(discountPercent)
              : "",
        },
      };
    });

  const bonusOptions = (
    Array.isArray(bonusInstallments) ? bonusInstallments : []
  )
    .filter(
      (row) =>
        bonusInstallmentHasRemainingToInvoice(row, invoiceContext) &&
        !excludeBonus.has(Number(row.id)),
    )
    .map((row) => {
      const amount = Number(row.bonusAmount) || 0;
      const remaining = resolveRemainingBonus(row, invoiceContext);
      const suggestAmount = remaining > 0.009 ? remaining : amount;
      const bonusTaxType = commissionGstTypeToInvoiceTaxType(
        row.gstType || row.gst_type,
      );
      const bonusTaxRate = resolveLineItemTaxRate(
        bonusTaxType,
        DEFAULT_COMMISSION_GST_PERCENT,
      );
      const due = row.dueDate || "—";
      const isPartial = remaining + 0.009 < amount && amount > 0;
      const amountLabel = isPartial
        ? `Remaining ${formatCurrency(suggestAmount, currency)} of ${formatCurrency(amount, currency)}`
        : formatCurrency(suggestAmount, currency);
      const bonusSubtitleParts = [`Installment #${row.installmentNo}`];
      if (due && due !== "—") bonusSubtitleParts.push(`Due ${due}`);
      if (providerName) bonusSubtitleParts.push(providerName);

      return {
        id: `bonus-${row.id}`,
        label: `Bonus #${row.installmentNo} · Due ${due} · ${amountLabel}${
          providerName ? ` · ${providerName}` : ""
        }`,
        displayTitle: "Bonus",
        displaySubtitle: bonusSubtitleParts.join(" · "),
        displayAmount: formatCurrency(suggestAmount, currency),
        displayAmountNote:
          isPartial && amount > 0
            ? `of ${formatCurrency(amount, currency)}`
            : "",
        fields: {
          description: buildBonusInvoiceLineDescription({
            studentName,
            studentId,
            studentDob,
            courseName,
            providerName,
            installmentNo: row.installmentNo,
            dueDate: row.dueDate,
            bonusAmount: suggestAmount,
          }),
          // Line total = remaining bonus (qty 1).
          unit_price: String(suggestAmount),
          quantity: "1",
          unit_price_locked: false,
          quantity_locked: true,
          tax_type: bonusTaxType,
          tax_rate: bonusTaxRate,
          gst_type: row.gstType || row.gst_type || "none",
          currency,
          fee_type: "bonus",
          application_id: applicationId || null,
          application_type: applicationType || "",
          commission_installment_id: null,
          bonus_installment_id: row.id,
          invoice_due_date: toSuggestionDate(row.dueDate),
        },
      };
    });

  return [
    ...commissionOptions,
    ...bonusOptions,
    ...(includeInquiryFees
      ? buildInquiryFeeSuggestions({
          applicationId,
          applicationType,
          currency,
          applicationFee,
          materialFee,
          // Inquiry fee billing comes from the inquiry-fee API (invoiced/remaining).
          // Do not fold plan commission-invoice scans in here — they can over-count
          // and hide application/material fee lines in the description picker.
          applicationFeeInvoiced: Number(applicationFeeInvoiced) || 0,
          materialFeeInvoiced: Number(materialFeeInvoiced) || 0,
          applicationFeeRemaining,
          materialFeeRemaining,
        })
      : []),
  ];
}
